/**
 * InvitationTokenPage Component
 *
 * Deep link handler for /invitations/:token from invitation emails.
 * Resolves the token, shows invitation details, and lets the user accept/decline.
 * If the user is not authenticated, redirects to login with returnTo.
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Mail, AlertCircle } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuthStatus } from "@/hooks/useAuthStatus";
import {
  resolveToken,
  acceptInvitation,
  declineInvitation,
} from "@/services/invitations";
import type { InvitationResponse } from "@/types/invitation";

export function InvitationTokenPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuthStatus();

  const [invitation, setInvitation] = useState<InvitationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);

  // Redirect to login page if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      // Store the return URL and navigate to the branded login page
      const returnTo = `/invitations/${token}`;
      sessionStorage.setItem("returnTo", returnTo);
      navigate("/login");
    }
  }, [authLoading, isAuthenticated, token, navigate]);

  // Fetch invitation once authenticated
  useEffect(() => {
    if (!isAuthenticated || !token) return;

    let cancelled = false;
    setLoading(true);

    resolveToken(token)
      .then((data) => {
        if (!cancelled) setInvitation(data);
      })
      .catch((err) => {
        if (!cancelled) {
          const detail =
            err?.response?.data?.detail ||
            err?.message ||
            "Invitation not found";
          setError(detail);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, token]);

  const handleAccept = async () => {
    if (!invitation) return;
    setAccepting(true);
    try {
      const result = await acceptInvitation(invitation.id);
      navigate(`/reflections/${result.reflection_id}`);
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Failed to accept invitation";
      setError(detail);
      setAccepting(false);
    }
  };

  const handleDecline = async () => {
    if (!invitation) return;
    setDeclining(true);
    try {
      await declineInvitation(invitation.id);
      navigate("/invitations");
    } catch {
      setDeclining(false);
    }
  };

  if (authLoading || (!isAuthenticated && !error)) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto max-w-lg px-4 py-12">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <Card className="p-6 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{error}</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate("/reflections")}
            >
              Go to Reflections
            </Button>
          </Card>
        )}

        {!loading && !error && invitation && (
          <Card className="p-6">
            <div className="text-center space-y-4">
              <Mail className="h-10 w-10 mx-auto text-primary" />
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  Shared Reflection
                </h2>
                <p className="text-muted-foreground mt-1">
                  <span className="font-medium text-foreground">
                    {invitation.sender_display_name || "Someone"}
                  </span>{" "}
                  shared a reflection with you
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-4">
                <p className="font-semibold text-foreground text-lg">
                  &ldquo;
                  {invitation.reflection_title || "Untitled Reflection"}
                  &rdquo;
                </p>
              </div>

              {invitation.status === "pending" && (
                <div className="flex items-center justify-center gap-3 pt-2">
                  <Button
                    onClick={handleAccept}
                    disabled={accepting || declining}
                  >
                    {accepting && (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    )}
                    Accept
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDecline}
                    disabled={accepting || declining}
                  >
                    {declining && (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    )}
                    Decline
                  </Button>
                </div>
              )}

              {invitation.status !== "pending" && (
                <p className="text-sm text-muted-foreground">
                  This invitation has been{" "}
                  {invitation.status === "accepted"
                    ? "accepted"
                    : invitation.status === "declined"
                      ? "declined"
                      : "expired"}
                  .
                </p>
              )}
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}
